/**
 * lib/dcGinReportExport.js
 * Excel (.xlsx via `xlsx`) and PDF (via `jspdf`) exports for the DC vs GIN
 * Report — same columns, applied filters, summary and statuses as the
 * on-screen report. Both take the full filtered row set (not one page).
 */
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";

export const REPORT_COLUMNS = [
  { field: "dc_date", header: "DC Date", kind: "date" },
  { field: "dc_code", header: "DC Number" },
  { field: "gin_date", header: "GIN Date", kind: "date" },
  { field: "gin_code", header: "GIN Number" },
  { field: "party", header: "Customer / Party" },
  { field: "item_code", header: "Item Code" },
  { field: "item_name", header: "Item Name" },
  { field: "dc_qty", header: "DC Quantity", kind: "number" },
  { field: "gin_qty", header: "GIN Quantity", kind: "number" },
  { field: "difference", header: "Difference", kind: "number" },
  { field: "status", header: "Status" },
  { field: "remarks", header: "Remarks" },
];

export const SUMMARY_FIELDS = [
  { key: "total_dcs", label: "Total DCs" },
  { key: "total_gins", label: "Total GINs" },
  { key: "total_dc_qty", label: "Total DC Quantity" },
  { key: "total_gin_qty", label: "Total GIN Quantity" },
  { key: "matched", label: "Matched" },
  { key: "partial", label: "Partial" },
  { key: "pending", label: "Pending" },
  { key: "mismatch", label: "Mismatch" },
  { key: "unmatched_gins", label: "Unmatched GINs" },
  { key: "total_difference", label: "Total Quantity Difference" },
];

export function fmtDate(v) {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

export function fmtQty(v) {
  const n = Number(v || 0);
  return Number.isInteger(n) ? String(n) : n.toFixed(3).replace(/\.?0+$/, "");
}

function cellText(row, col) {
  const v = row[col.field];
  if (col.field === "gin_date" && (row.gin_dates || []).length > 1) {
    return row.gin_dates.map(fmtDate).join(", ");
  }
  if (col.kind === "date") return fmtDate(v);
  if (col.kind === "number") return fmtQty(v);
  return v == null ? "" : String(v);
}

function stamp() {
  return new Date().toISOString().slice(0, 10);
}

export function exportReportExcel({ dcRows, unmatchedRows, summary, filterLines }) {
  const wb = XLSX.utils.book_new();

  const summaryAoa = [
    ["DC vs GIN Report"],
    [`Generated ${new Date().toLocaleString("en-IN")}`],
    [],
    ["Filters"],
    ...filterLines.map(([k, v]) => [k, v]),
    [],
    ["Summary"],
    ...SUMMARY_FIELDS.map((f) => [f.label, Number(summary?.[f.key] || 0)]),
  ];
  const ws0 = XLSX.utils.aoa_to_sheet(summaryAoa);
  ws0["!cols"] = [{ wch: 28 }, { wch: 40 }];
  XLSX.utils.book_append_sheet(wb, ws0, "Summary");

  const sheetFor = (rows) => {
    const aoa = [
      REPORT_COLUMNS.map((c) => c.header),
      ...rows.map((r) => REPORT_COLUMNS.map((c) => (c.kind === "number" ? Number(r[c.field] || 0) : cellText(r, c)))),
    ];
    if (rows.length) {
      const sum = (f) => rows.reduce((a, r) => a + Number(r[f] || 0), 0);
      aoa.push(REPORT_COLUMNS.map((c, i) => (i === 0 ? "Total" : c.kind === "number" ? sum(c.field) : "")));
    }
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!cols"] = REPORT_COLUMNS.map((c) => ({ wch: c.field === "remarks" ? 40 : c.field === "item_name" ? 30 : 16 }));
    return ws;
  };
  XLSX.utils.book_append_sheet(wb, sheetFor(dcRows), "DC vs GIN");
  XLSX.utils.book_append_sheet(wb, sheetFor(unmatchedRows), "Unmatched GINs");
  XLSX.writeFile(wb, `dc_vs_gin_report_${stamp()}.xlsx`);
}

const STATUS_RGB = {
  Matched: [22, 163, 74],
  Partial: [217, 119, 6],
  Pending: [100, 116, 139],
  Mismatch: [220, 38, 38],
  "Unmatched GIN": [124, 58, 237],
};

export function exportReportPdf({ dcRows, unmatchedRows, summary, filterLines }) {
  const doc = new jsPDF({ unit: "pt", format: "a4", orientation: "landscape" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 28;
  let y = 40;

  doc.setFont("helvetica", "bold"); doc.setFontSize(16); doc.setTextColor(15, 23, 42);
  doc.text("DC vs GIN Report", margin, y);
  doc.setFont("helvetica", "normal"); doc.setFontSize(8.5); doc.setTextColor(100, 116, 139);
  doc.text(`Generated ${new Date().toLocaleString("en-IN")}`, pageW - margin, y, { align: "right" });
  y += 10;
  doc.setDrawColor(37, 99, 235); doc.setLineWidth(1.2);
  doc.line(margin, y, pageW - margin, y);
  y += 16;

  // Filters
  doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(71, 85, 105);
  doc.text("Filters:", margin, y);
  doc.setFont("helvetica", "normal"); doc.setTextColor(15, 23, 42);
  const filterText = filterLines.map(([k, v]) => `${k}: ${v}`).join("   ·   ");
  const fl = doc.splitTextToSize(filterText, pageW - margin * 2 - 44);
  fl.forEach((line, i) => doc.text(line, margin + 44, y + i * 11));
  y += fl.length * 11 + 8;

  // Summary cards — 5 per row
  const perRow = 5;
  const gap = 8;
  const cardW = (pageW - margin * 2 - gap * (perRow - 1)) / perRow;
  const cardH = 34;
  SUMMARY_FIELDS.forEach((f, i) => {
    const cx = margin + (i % perRow) * (cardW + gap);
    const cy = y + Math.floor(i / perRow) * (cardH + gap);
    doc.setFillColor(248, 250, 252); doc.setDrawColor(226, 232, 240);
    doc.roundedRect(cx, cy, cardW, cardH, 3, 3, "FD");
    doc.setFont("helvetica", "normal"); doc.setFontSize(7); doc.setTextColor(100, 116, 139);
    doc.text(f.label.toUpperCase(), cx + 8, cy + 12);
    doc.setFont("helvetica", "bold"); doc.setFontSize(12); doc.setTextColor(15, 23, 42);
    doc.text(fmtQty(summary?.[f.key]), cx + 8, cy + 27);
  });
  y += Math.ceil(SUMMARY_FIELDS.length / perRow) * (cardH + gap) + 8;

  const widths = { dc_date: 52, dc_code: 58, gin_date: 52, gin_code: 62, party: 92, item_code: 64, item_name: 104,
    dc_qty: 58, gin_qty: 60, difference: 48, status: 52, remarks: 0 };
  const fixed = Object.values(widths).reduce((a, b) => a + b, 0);
  widths.remarks = pageW - margin * 2 - fixed;
  const numeric = new Set(["dc_qty", "gin_qty", "difference"]);
  const lineH = 9;

  const drawHeader = () => {
    doc.setFillColor(241, 245, 249); doc.setDrawColor(226, 232, 240);
    doc.rect(margin, y, pageW - margin * 2, 18, "FD");
    doc.setFont("helvetica", "bold"); doc.setFontSize(7); doc.setTextColor(71, 85, 105);
    let cx = margin;
    REPORT_COLUMNS.forEach((c) => {
      const w = widths[c.field];
      if (numeric.has(c.field)) doc.text(c.header, cx + w - 4, y + 12, { align: "right" });
      else doc.text(c.header, cx + 4, y + 12);
      cx += w;
    });
    y += 18;
  };

  const drawTable = (title, rows) => {
    if (y > pageH - 80) { doc.addPage(); y = 40; }
    doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(15, 23, 42);
    doc.text(`${title} (${rows.length})`, margin, y);
    y += 8;
    drawHeader();
    if (!rows.length) {
      doc.setFont("helvetica", "italic"); doc.setFontSize(8); doc.setTextColor(148, 163, 184);
      doc.text("No rows.", margin + 4, y + 12);
      y += 24;
      return;
    }
    rows.forEach((r) => {
      doc.setFont("helvetica", "normal"); doc.setFontSize(7);
      const cells = REPORT_COLUMNS.map((c) => doc.splitTextToSize(cellText(r, c), widths[c.field] - 8));
      const h = Math.max(...cells.map((l) => l.length)) * lineH + 6;
      if (y + h > pageH - 30) { doc.addPage(); y = 40; drawHeader(); doc.setFont("helvetica", "normal"); doc.setFontSize(7); }
      doc.setDrawColor(226, 232, 240);
      doc.line(margin, y + h, pageW - margin, y + h);
      let cx = margin;
      REPORT_COLUMNS.forEach((c, i) => {
        const w = widths[c.field];
        if (c.field === "status") {
          doc.setFont("helvetica", "bold");
          doc.setTextColor(...(STATUS_RGB[r.status] || [15, 23, 42]));
        } else {
          doc.setFont("helvetica", "normal");
          doc.setTextColor(15, 23, 42);
        }
        cells[i].forEach((line, li) => {
          if (numeric.has(c.field)) doc.text(line, cx + w - 4, y + 10 + li * lineH, { align: "right" });
          else doc.text(line, cx + 4, y + 10 + li * lineH);
        });
        cx += w;
      });
      y += h;
    });
    // Totals
    const sum = (f) => rows.reduce((a, r) => a + Number(r[f] || 0), 0);
    doc.setFont("helvetica", "bold"); doc.setFontSize(7.5); doc.setTextColor(15, 23, 42);
    if (y + 16 > pageH - 30) { doc.addPage(); y = 40; }
    let cx = margin;
    REPORT_COLUMNS.forEach((c, i) => {
      const w = widths[c.field];
      if (i === 0) doc.text("Total", cx + 4, y + 11);
      if (numeric.has(c.field)) doc.text(fmtQty(sum(c.field)), cx + w - 4, y + 11, { align: "right" });
      cx += w;
    });
    y += 36;
  };

  drawTable("DC vs GIN", dcRows);
  drawTable("Unmatched GINs", unmatchedRows);

  const pages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= pages; p += 1) {
    doc.setPage(p);
    doc.setFont("helvetica", "normal"); doc.setFontSize(7); doc.setTextColor(148, 163, 184);
    doc.text(`Page ${p} of ${pages}`, pageW - margin, pageH - 14, { align: "right" });
  }
  doc.save(`dc_vs_gin_report_${stamp()}.pdf`);
}
